import { useState, useEffect } from "react";
import { getApiUrl } from "../utils/api";
const API = getApiUrl();
import { useNavigate } from "react-router-dom";
import { ArrowLeft, MessageSquare, Trash2, Check, X, Search, Filter } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";


export default function MessagesWall() {
  const navigate = useNavigate();
  const [site, setSite] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const siteRes = await fetch(`${API}/sites/my`, { credentials: 'include' });
        if (siteRes.ok) {
          const siteData = await siteRes.json();
          if (siteData) {
            setSite(siteData);
            const messagesRes = await fetch(`${API}/sites/${siteData.site_id}/guestbook?approved_only=false`, { credentials: 'include' });
            if (messagesRes.ok) {
              const messagesData = await messagesRes.json();
              setMessages(messagesData);
            }
          } else {
            navigate('/onboarding');
          }
        }
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [navigate]);

  const handleApprove = async (messageId, approve) => {
    try {
      const res = await fetch(`${API}/sites/${site.site_id}/guestbook/${messageId}/approve?approve=${approve}`, {
        method: 'PUT',
        credentials: 'include'
      });

      if (res.ok) {
        setMessages(prev => prev.map(m => 
          m.message_id === messageId ? { ...m, is_approved: approve } : m
        ));
        toast.success(approve ? "Mensagem aprovada!" : "Mensagem rejeitada");
      }
    } catch (error) {
      toast.error("Erro ao atualizar mensagem");
    }
  };

  const handleDelete = async (messageId) => {
    if (!window.confirm("Remover esta mensagem permanentemente?")) return;

    try {
      const res = await fetch(`${API}/sites/${site.site_id}/guestbook/${messageId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (res.ok) {
        setMessages(prev => prev.filter(m => m.message_id !== messageId));
        toast.success("Mensagem removida");
      }
    } catch (error) {
      toast.error("Erro ao remover mensagem");
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleDateString('pt-BR', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredMessages = messages.filter(m => {
    const matchesSearch = m.author_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         m.message.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (filter === "approved") return matchesSearch && m.is_approved;
    if (filter === "pending") return matchesSearch && !m.is_approved;
    return matchesSearch;
  });

  const approvedCount = messages.filter(m => m.is_approved).length;
  const pendingCount = messages.filter(m => !m.is_approved).length;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="w-16 h-16 border-4 border-[#F3C9CF] border-t-[#B3121B] rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="messages-wall">
      {/* Header */}
      <header className="bg-white border-b border-[#F3C9CF]/30 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/dashboard')}
              className="text-[#6E0F1A]"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <MessageSquare className="w-6 h-6 text-[#B3121B]" />
              <span className="font-serif text-xl text-[#6E0F1A]">Mural de Recados</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <Card className="card-dashboard">
            <CardContent className="pt-6">
              <p className="text-sm text-[#6E0F1A]/60 uppercase tracking-wide">Total de Mensagens</p>
              <p className="text-3xl font-semibold text-[#6E0F1A] mt-1">{messages.length}</p>
            </CardContent>
          </Card>
          <Card className="card-dashboard">
            <CardContent className="pt-6">
              <p className="text-sm text-[#6E0F1A]/60 uppercase tracking-wide">Aprovadas</p>
              <p className="text-3xl font-semibold text-[#567D50] mt-1">{approvedCount}</p>
            </CardContent>
          </Card>
          <Card className="card-dashboard">
            <CardContent className="pt-6">
              <p className="text-sm text-[#6E0F1A]/60 uppercase tracking-wide">Pendentes</p>
              <p className="text-3xl font-semibold text-yellow-600 mt-1">{pendingCount}</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        {messages.length > 0 && (
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6E0F1A]/40" />
              <Input
                placeholder="Buscar mensagens..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-wedding pl-10"
                data-testid="search-input"
              />
            </div>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="input-wedding w-full sm:w-48">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filtrar" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas ({messages.length})</SelectItem>
                <SelectItem value="approved">Aprovadas ({approvedCount})</SelectItem>
                <SelectItem value="pending">Pendentes ({pendingCount})</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Messages List */}
        {messages.length === 0 ? (
          <Card className="card-dashboard text-center py-16">
            <CardContent>
              <div className="w-20 h-20 bg-[#F3C9CF]/30 rounded-full flex items-center justify-center mx-auto mb-6">
                <MessageSquare className="w-10 h-10 text-[#B3121B]" />
              </div>
              <h2 className="font-serif text-2xl text-[#6E0F1A] mb-3">
                Nenhuma mensagem ainda
              </h2>
              <p className="text-[#6E0F1A]/70 mb-6">
                Os convidados podem deixar mensagens através do site público do seu evento
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredMessages.map((msg) => (
              <Card key={msg.message_id} className="card-dashboard" data-testid={`message-${msg.message_id}`}>
                <CardContent className="py-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-[#6E0F1A]">{msg.author_name}</h3>
                        {msg.is_approved ? (
                          <Badge className="bg-[#567D50]">Aprovada</Badge>
                        ) : (
                          <Badge variant="secondary" className="bg-yellow-100 text-yellow-700">Pendente</Badge>
                        )}
                      </div>
                      <p className="text-[#6E0F1A]/80 mb-3">{msg.message}</p>
                      <p className="text-xs text-[#6E0F1A]/50">{formatDate(msg.created_at)}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {!msg.is_approved && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleApprove(msg.message_id, true)}
                          className="text-[#567D50] hover:bg-[#567D50]/10"
                          title="Aprovar"
                          data-testid={`approve-${msg.message_id}`}
                        >
                          <Check className="w-5 h-5" />
                        </Button>
                      )}
                      {msg.is_approved && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleApprove(msg.message_id, false)}
                          className="text-yellow-600 hover:bg-yellow-50"
                          title="Desaprovar"
                          data-testid={`reject-${msg.message_id}`}
                        >
                          <X className="w-5 h-5" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(msg.message_id)}
                        className="text-[#6E0F1A]/60 hover:text-red-500"
                        title="Excluir"
                        data-testid={`delete-${msg.message_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
